import { isValidObjectId } from "../find_unique_ids_in_list";
import { includesIgnoreCase } from "../utils";

// The fields of a lesson's related incident that these read.
type IncidentRecord = {
	_id?: unknown;
	report_number?: unknown;
	title?: unknown;
	operator?: unknown;
};

// The fields of the signed-in tenant that canOpenIncidentPage reads.
type TenantAccess = {
	subscriptions?: unknown;
	modules_authorized_users?: Record<string, unknown> | null;
	enabled_reporting_types?: unknown;
};

export type ShownIncident = {
	id: string;
	number: string;
	title?: string;
};

const isText = (value: unknown): value is string =>
	typeof value === "string" && value.trim() !== "";

// The first of the values that can be shown as one of a lesson's facts: text
// that is neither blank nor a database id. A lesson stores ids that its lookup
// names, and an imported lesson stores some names, such as its asset's, as
// text. An id the lookup couldn't name, such as another tenant's, shows as
// nothing.
export const getLessonFieldText = (...values: unknown[]): string | undefined =>
	values.find(
		(value): value is string => isText(value) && !isValidObjectId(value)
	);

// The id of the incident a lesson is related to. Lessons store only the id,
// and only older ones have it: the form field that set it is gone.
export function getLinkedIncidentId(
	lesson: { linked_incident?: unknown } | null | undefined
): string | undefined {
	const id = lesson?.linked_incident;
	return typeof id === "string" && isValidObjectId(id) ? id : undefined;
}

// The related incident as a lesson's page names it: its id, which the page
// puts in a link, its number, and its title when it has one. Nothing for an
// incident outside the viewer's tenants, so another tenant's incident isn't
// named on the page.
export function getShownLinkedIncident(
	incident: IncidentRecord | null | undefined,
	tenantKeys: string[]
): ShownIncident | undefined {
	if (typeof incident?._id !== "string" || !isValidObjectId(incident._id)) {
		return undefined;
	}
	if (!isText(incident.report_number)) return undefined;
	const { operator } = incident;
	if (typeof operator !== "string" || !tenantKeys.includes(operator)) {
		return undefined;
	}
	return {
		id: incident._id,
		number: incident.report_number,
		title: isText(incident.title) ? incident.title : undefined,
	};
}

// Whether the viewer could open an incident's page. Mirrors its route's guard
// (ProtectedModuleRoute with moduleKey "reporting" and reportingType
// "incident_report", in App.tsx), which reads the signed-in tenant. This is UX
// only: the server checks neither.
export function canOpenIncidentPage(
	tenant: TenantAccess | null | undefined,
	email: string | null | undefined,
	isSuperAdmin: boolean
): boolean {
	const subscriptions = tenant?.subscriptions;
	if (!Array.isArray(subscriptions) || !subscriptions.includes("reporting")) {
		return false;
	}
	if (isSuperAdmin) return true;
	const types = tenant?.enabled_reporting_types;
	return (
		includesIgnoreCase(tenant?.modules_authorized_users?.reporting, email) &&
		Array.isArray(types) &&
		types.includes("incident_report")
	);
}
